import { FileDownload, Groups, RestartAlt } from "@mui/icons-material";
import { Box, useTheme } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SaveAsDefaultDialog } from "../../../../../components/SaveAsDefault/SaveAsDefaultDialog";
import { type DownloadFormat, DownloadFormatButton } from "../../../../../components/UIToolkit/Clickable/DownloadFormatButton";
import { MultiSizePopup } from "../../../../../components/UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../../../../../components/UIToolkit/Popups/MultiSizePopup/utils";
import { SpreadsheetPreview, type SpreadsheetPreviewHandle } from "../../../../../components/UIToolkit/Previews/SpreadsheetPreview";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../../contexts/ChosenDepartment";
import { generatePrintoutPdf } from "../../../../../downloads/downloadDayViewPdf";
import { handleDownload } from "../../../../../downloads/utils";
import { createWeekStaffScheduleExport, WeekScheduleExportTooLargeError } from "../../../../../downloads/weekStaffScheduleExport/createWeekStaffScheduleExport";
import {
	DEFAULT_PAST_WEEKS,
	filterWeeksToRange,
	getWeekBoundaryWeekdays,
	resolveDefaultRangeStart,
	snapToAvailableWeekStart,
} from "../../../../../downloads/weekStaffScheduleExport/model/weekRange";
import type { CreateWeekStaffScheduleExportArgs, ExportAnalyticsSink } from "../../../../../downloads/weekStaffScheduleExport/types";
import { useLocalizedWeekDays } from "../../../../../hooks/useDateLocalization";
import { useErrorSnackbar } from "../../../../../hooks/useErrorSnackbar";
import { useFormatPersonName } from "../../../../../hooks/useNameFormat";
import { ChangesSource } from "../../../../../utils/models";
import { DAYS_PER_WEEK, type WeekMeta, type WeekScheduleRowContext } from "../weekScheduleCellResolver";
import { buildWeekScheduleExportColors } from "./exportColors";
import { useWeekStaffScheduleExportPreview } from "./useWeekStaffScheduleExportPreview";
import { useWeekStaffScheduleExportSettings } from "./useWeekStaffScheduleExportSettings";
import { WeekStaffScheduleExportSettings } from "./WeekStaffScheduleExportSettings";

type Props = {
	weeks: WeekMeta[];
	rowContext: WeekScheduleRowContext;
	dayStart: string;
	/** Days the visible week is shifted past Sunday, so the sheet's columns match the grid's. */
	offset: number;
};

const TEST_ID_PREFIX = "week-staff-schedule-export";
const SETTINGS_PANEL_WIDTH = 420;
const FILE_DATE_FORMAT = "yyyy-MM-dd";

/** The last day of the week a given week start opens. */
const lastDayOfWeek = (weekStart: DateTime | undefined): DateTime | undefined => weekStart?.plus({ days: DAYS_PER_WEEK - 1 });

export const WeekStaffScheduleExportButton: FC<Props> = ({ weeks, rowContext, dayStart, offset }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const showErrorSnackbar = useErrorSnackbar();
	const weekDayNames = useLocalizedWeekDays("cccc");

	const [isOpen, setIsOpen] = useState(false);
	// `null` means the default range (recent past through the last week the grid shows). Kept as a
	// sentinel rather than materializing the ends so the range keeps tracking the grid if its weeks
	// change, and so Reset has something to clear. Deliberately NOT persisted — it tracks the grid.
	const [weekRange, setWeekRange] = useState<{ from: DateTime; to: DateTime } | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isSaveAsDefaultDialogOpen, setIsSaveAsDefaultDialogOpen] = useState(false);
	// A programmatic width reset re-enters the preview's change callback; the preview swallows that
	// echo when armed, so it can't loop.
	const previewRef = useRef<SpreadsheetPreviewHandle>(null);

	const { nurse, departmentId } = rowContext;
	const formatPersonName = useFormatPersonName();
	const hasMultipleJobs = (nurse.jobs?.length ?? 0) > 1;
	const nurseName = formatPersonName(nurse);

	const earliestWeekStart = weeks.at(0)?.weekStart;
	const latestWeekStart = weeks.at(-1)?.weekStart;
	// Opens on the recent past rather than the whole history; the picker still reaches back to `earliest`.
	const defaultRangeFrom = useMemo(
		() => resolveDefaultRangeStart(weeks, rowContext.todayStaffingDate, DEFAULT_PAST_WEEKS),
		[weeks, rowContext.todayStaffingDate],
	);
	// Both ends of the range are tracked as week STARTS; only the "to" picker displays a week end.
	const rangeFrom = weekRange?.from ?? defaultRangeFrom;
	const rangeTo = weekRange?.to ?? latestWeekStart;
	const boundaryWeekdays = useMemo(() => getWeekBoundaryWeekdays(weeks), [weeks]);

	/** The weeks that actually reach the sheet. Every week's `absoluteWeekIndex` survives the filter. */
	const exportWeeks = useMemo(() => filterWeeksToRange(weeks, rangeFrom, rangeTo), [weeks, rangeFrom, rangeTo]);

	// The pickers only offer week boundaries, so a pick already identifies a week; resolving it through
	// the week that contains it also covers the "to" picker handing us a week's LAST day. The other end
	// is pushed along if a pick would invert the range (the pickers bound each other, so this is a backstop).
	const handleRangeChange = useCallback(
		(edge: "from" | "to") => (picked: DateTime) => {
			const snapped = snapToAvailableWeekStart(weeks, picked);
			if (!snapped) return;
			setWeekRange((previous) => {
				const current = { from: previous?.from ?? defaultRangeFrom, to: previous?.to ?? latestWeekStart };
				if (!current.from || !current.to) return previous;
				return edge === "from"
					? { from: snapped, to: current.to < snapped ? snapped : current.to }
					: { from: current.from > snapped ? snapped : current.from, to: snapped };
			});
		},
		[weeks, defaultRangeFrom, latestWeekStart],
	);

	const analyticsProps = useMemo(
		() => ({ nurse_id: nurse.id, department_id: departmentId, week_count: exportWeeks.length }),
		[nurse.id, departmentId, exportWeeks.length],
	);

	const reportFailure = useCallback(
		(error: unknown) => {
			const message = error instanceof WeekScheduleExportTooLargeError ? t("profile.schedule.export.too.many.rows") : t("profile.schedule.export.failed");
			trackError("week_staff_schedule_export_preview_error", { ...analyticsProps, error_message: error instanceof Error ? error.message : String(error) });
			showErrorSnackbar(message, { action: "week schedule export failed", source: ChangesSource.Profile }, error);
		},
		[t, trackError, showErrorSnackbar, analyticsProps],
	);

	// A settings save failing is a different problem from the sheet failing to build, and gets its own
	// message — otherwise a save error reads as "your export is broken".
	const reportSettingsFailure = useCallback(
		(error: unknown) => {
			trackError("week_staff_schedule_export_settings_error", { ...analyticsProps, error_message: error instanceof Error ? error.message : String(error) });
			showErrorSnackbar(
				t("profile.schedule.export.settings.save.failed"),
				{ action: "week schedule export settings save failed", source: ChangesSource.Profile },
				error,
			);
		},
		[t, trackError, showErrorSnackbar, analyticsProps],
	);

	// Runs only on a successful restore, and synchronously before the rebuilt preview can report back —
	// so the echo guard is armed exactly when there is a rebuild to swallow, and a failed restore
	// leaves neither the guard armed nor the date range cleared.
	const handleRestored = useCallback(() => {
		previewRef.current?.suppressNextColumnWidthsChange();
		setWeekRange(null);
		track("week_staff_schedule_export_settings_reset", analyticsProps);
	}, [track, analyticsProps]);

	const { settings, columnWidthOverrides, updateSetting, setColumnWidths, resetToDepartmentDefault, canSaveAsDepartmentDefault, saveAsDepartmentDefault } =
		useWeekStaffScheduleExportSettings({
			onSaved: () => track("week_staff_schedule_export_settings_saved", analyticsProps),
			onResetToDepartmentDefault: handleRestored,
			onSavedAsDepartmentDefault: () => track("week_staff_schedule_export_saved_as_department_default", analyticsProps),
			onError: reportSettingsFailure,
		});

	// `useAnalytics` returns fresh callbacks whenever an ancestor re-renders (its analytic values are
	// memoized on a props object). These get handed to the builder inside the memoized build args, so
	// they go through a ref: a changing identity there would rebuild the workbook on every render.
	const analyticsRef = useRef({ track, trackError });
	analyticsRef.current = { track, trackError };
	const analyticsSink = useMemo<ExportAnalyticsSink>(
		() => ({
			track: (event, props) => analyticsRef.current.track(event, props),
			trackError: (event, props) => analyticsRef.current.trackError(event, props),
		}),
		[],
	);

	const labels = useMemo(() => {
		const firstWeekStart = exportWeeks.at(0)?.weekStart;
		const lastWeekEnd = exportWeeks.at(-1)?.weekStart.plus({ days: DAYS_PER_WEEK - 1 });
		return {
			title: t("profile.schedule.export.title", { name: nurseName }),
			department: chosenDepartment.display_name,
			subtitle:
				firstWeekStart && lastWeekEnd
					? t("profile.schedule.export.subtitle.range", { from: firstWeekStart.toFormat("MMM d, yyyy"), to: lastWeekEnd.toFormat("MMM d, yyyy") })
					: "",
			// Stamped per build, in the facility's timezone — so the sheet says when it was produced
			// and the preview shows the same stamp the downloaded file carries.
			generatedAt: t("profile.schedule.export.generated.at", {
				timestamp: DateTime.now().setZone(chosenDepartment.timezone).toFormat("MMM d, yyyy h:mm a"),
			}),
			sheetName: t("profile.schedule.export.sheet.name"),
			timeOff: t("time.off"),
			prefers: t("profile.schedule.export.prefers"),
			// Rotate the Sunday-first names to the visible week start so headers line up with the dates.
			weekDayHeaders: Array.from({ length: DAYS_PER_WEEK }, (_, index) => weekDayNames[(index + offset) % DAYS_PER_WEEK]),
		};
	}, [t, nurseName, exportWeeks, weekDayNames, offset, chosenDepartment.display_name, chosenDepartment.timezone]);

	const buildArgs = useMemo<CreateWeekStaffScheduleExportArgs | null>(() => {
		if (!isOpen) return null;
		const { includeScheduled, includeActual, includeTimeOff, includePreferences, includeLocation, includeJobName, ...pageSetup } = settings;
		return {
			weeks: exportWeeks,
			rowContext,
			dayStart,
			content: { includeScheduled, includeActual, includeTimeOff, includePreferences },
			details: { includeLocation, includeJobName },
			labels,
			colors: buildWeekScheduleExportColors(theme),
			pageSetup,
			columnWidthOverrides,
			analytics: analyticsSink,
			nurseId: nurse.id,
			departmentId,
		};
	}, [isOpen, settings, exportWeeks, rowContext, dayStart, labels, theme, columnWidthOverrides, analyticsSink, nurse.id, departmentId]);

	const { sheets, isLoading } = useWeekStaffScheduleExportPreview({ buildArgs, onError: reportFailure });

	const handleOpen = useCallback(() => {
		setIsOpen(true);
		track("week_staff_schedule_export_opened", analyticsProps);
	}, [track, analyticsProps]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
		track("week_staff_schedule_export_closed", analyticsProps);
	}, [track, analyticsProps]);

	const handleConfirmSaveAsDefault = useCallback(async (): Promise<void> => {
		setIsSaveAsDefaultDialogOpen(false);
		await saveAsDepartmentDefault();
	}, [saveAsDepartmentDefault]);

	const fileName = useMemo(() => {
		const slug = nurseName.trim().toLowerCase().replace(/\s+/g, "_");
		const from = exportWeeks.at(0)?.weekStart.toFormat(FILE_DATE_FORMAT) ?? "";
		return `weekly_schedule_${slug}_${from}`;
	}, [nurseName, exportWeeks]);

	const runDownload = useCallback(
		async (format: DownloadFormat): Promise<void> => {
			if (!buildArgs) return;
			setIsDownloading(true);
			track("week_staff_schedule_export_download_start", { ...analyticsProps, format, orientation: settings.orientation });
			try {
				if (format === "pdf") {
					// The same fit-to settings `applyWeekSchedulePageSetup` writes into the xlsx. Passing nulls here
					// meant a user who asked for "2 pages wide" got a 2-page-wide spreadsheet and a 1-page-wide PDF.
					handleDownload(
						generatePrintoutPdf(sheets, settings.orientation, settings.fitToPage, settings.fitToWidth, settings.fitToHeight),
						`${fileName}.pdf`,
						"application/pdf",
					);
				} else {
					handleDownload(await createWeekStaffScheduleExport(buildArgs), `${fileName}.xlsx`);
				}
				track("week_staff_schedule_export_download_success", { ...analyticsProps, format });
				handleClose();
			} catch (error) {
				reportFailure(error);
			} finally {
				setIsDownloading(false);
			}
		},
		[
			buildArgs,
			sheets,
			settings.orientation,
			settings.fitToPage,
			settings.fitToWidth,
			settings.fitToHeight,
			fileName,
			track,
			analyticsProps,
			handleClose,
			reportFailure,
		],
	);

	// The PDF is rendered from the preview sheets, so it needs them present.
	const canDownload = !isLoading && sheets.length > 0 && !isDownloading;

	const body = (
		<Box sx={{ width: { xxl: "80vw", xs: "96vw" }, height: "80vh", display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
			<Box sx={{ flex: 1, display: "flex", gap: 2, minHeight: 0 }}>
				<Box sx={{ width: SETTINGS_PANEL_WIDTH, flexShrink: 0, overflowY: "auto", pr: 2 }}>
					<WeekStaffScheduleExportSettings
						settings={settings}
						onChange={updateSetting}
						hasMultipleJobs={hasMultipleJobs}
						dateRange={{
							from: rangeFrom,
							// Shown as the week's last day, so the field reads as a real end-of-range date.
							to: lastDayOfWeek(rangeTo),
							earliest: earliestWeekStart,
							latest: lastDayOfWeek(latestWeekStart),
							boundaryWeekdays,
							onFromChange: handleRangeChange("from"),
							onToChange: handleRangeChange("to"),
						}}
					/>
				</Box>
				<SpreadsheetPreview
					ref={previewRef}
					dataTestId={`${TEST_ID_PREFIX}-preview`}
					sheets={sheets}
					isLoading={isLoading}
					showSheetTabs={false}
					columnWidths={columnWidthOverrides}
					onColumnWidthsChange={setColumnWidths}
					errorLabel={t("profile.schedule.export.failed")}
					sx={{ flex: 1, minWidth: 0 }}
				/>
			</Box>

			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{/* Resets the panel, the dragged column widths and the date range — back to the department default. */}
					<SystemButton
						data-testid={`${TEST_ID_PREFIX}-reset-settings`}
						buttonVariant="secondary"
						onClick={() => void resetToDepartmentDefault()}
						leadingIcon={<RestartAlt />}
					>
						{t("reset.to.default")}
					</SystemButton>
					{canSaveAsDepartmentDefault && (
						<SystemButton
							data-testid={`${TEST_ID_PREFIX}-save-as-department-default`}
							buttonVariant="borderless"
							onClick={() => setIsSaveAsDefaultDialogOpen(true)}
							leadingIcon={<Groups />}
						>
							{t("profile.schedule.export.save.as.department.default")}
						</SystemButton>
					)}
				</Box>
				<DownloadFormatButton
					dataTestIdPrefix={TEST_ID_PREFIX}
					formats={["xlsx", "pdf"]}
					onDownload={(format) => void runDownload(format)}
					isDownloading={isDownloading}
					disabled={!canDownload}
				/>
			</Box>
		</Box>
	);

	return (
		<>
			{/*
				`aria-label` as well as `tooltip`: SystemButton renders a tooltip by wrapping the button in a
				Box, so MUI puts its generated `aria-label` on that wrapper div instead of the button — which
				leaves an icon-only button with no accessible name at all.
			*/}
			<SystemButton
				data-testid={`${TEST_ID_PREFIX}-button`}
				buttonVariant="borderless"
				size="small"
				onClick={handleOpen}
				leadingIcon={<FileDownload sx={{ color: "primary.main" }} />}
				tooltip={t("profile.schedule.export.button")}
				aria-label={t("profile.schedule.export.button")}
			/>
			{isOpen && (
				<MultiSizePopup
					data-testid={`${TEST_ID_PREFIX}-dialog`}
					open={isOpen}
					onClose={handleClose}
					title={t("profile.schedule.export.dialog.title")}
					disableMinimize
					closeOnEscape
					bodyRenderer={(sizeState) => (sizeState === MultiSizePopupState.MAXIMIZED ? body : null)}
				/>
			)}
			{canSaveAsDepartmentDefault && (
				<SaveAsDefaultDialog open={isSaveAsDefaultDialogOpen} onClose={() => setIsSaveAsDefaultDialogOpen(false)} onConfirm={handleConfirmSaveAsDefault} />
			)}
		</>
	);
};
